import type {
  GeoJsonFeatureCollection,
  MapMarker,
  MarkerArcLayerOptions,
} from '@gpx-spa/geo-domain'
import type { MapInteractionController } from '../shared/map-interaction-controller.js'
import {
  planArcSegments,
  type ArcSegmentPlan,
} from '../shared/marker-arc-plan.js'
import { bindLeafletPathInteractions } from './leaflet-map-interactions.js'
import L from 'leaflet'
import 'leaflet.geodesic'

export {
  MARKER_ARC_DEFAULTS,
  groupMarkersByArcGroup,
  mergedArcStyle,
  planArcSegments,
  resolveMarkerArcLineColor,
} from '../shared/marker-arc-plan.js'
export type { ArcSegmentPlan } from '../shared/marker-arc-plan.js'

export type MarkerArcSyncContext = {
  markers: MapMarker[]
  arcOptions?: MarkerArcLayerOptions
  clusterGroup?: L.MarkerClusterGroup
  markerLeaflets?: Map<string, L.Marker>
  interactionController?: MapInteractionController | null
  withInteractiveVectorPane: (opts: L.PathOptions) => L.PathOptions
}

function syntheticArcFeature(
  segment: ArcSegmentPlan,
): GeoJsonFeatureCollection['features'][number] {
  return {
    type: 'Feature',
    properties: {
      ...segment.payload,
      map: {
        lineGeodesic: true,
        lineColor: segment.style.lineColor,
        lineWidth: segment.style.lineWidth,
        lineOpacity: segment.style.lineOpacity,
        lineGeodesicSteps: segment.style.lineGeodesicSteps,
      },
    },
    geometry: {
      type: 'LineString',
      coordinates: [
        [segment.from.lng, segment.from.lat],
        [segment.to.lng, segment.to.lat],
      ],
    },
  }
}

export function resolveMarkerArcLatLng(
  layerId: string,
  marker: MapMarker,
  clusterGroup: L.MarkerClusterGroup | undefined,
  markerLeaflets: Map<string, L.Marker> | undefined,
): L.LatLng {
  const leafletMarker = markerLeaflets?.get(`${layerId}::${marker.id}`)
  if (leafletMarker && clusterGroup) {
    const parent = clusterGroup.getVisibleParent(leafletMarker)
    if (parent) return parent.getLatLng()
  }
  return L.latLng(marker.position.lat, marker.position.lng)
}

export function rebuildMarkerArcLayerGroup(
  layerId: string,
  layerGroup: L.LayerGroup,
  context: MarkerArcSyncContext,
): void {
  layerGroup.clearLayers()
  const segments = planArcSegments(context.markers, (marker) =>
    resolveMarkerArcLatLng(layerId, marker, context.clusterGroup, context.markerLeaflets),
    context.arcOptions,
  )
  for (const segment of segments) {
    const feature = syntheticArcFeature(segment)
    const path = new L.Geodesic(
      [[segment.from, segment.to]],
      context.withInteractiveVectorPane({
        color: segment.style.lineColor,
        weight: segment.style.lineWidth,
        opacity: segment.style.lineOpacity,
        steps: segment.style.lineGeodesicSteps,
        wrap: false,
      }),
    )
    if (context.interactionController?.hasInteractions()) {
      bindLeafletPathInteractions(layerId, path, feature, context.interactionController)
    }
    layerGroup.addLayer(path)
  }
}

export type MarkerArcConnector = {
  getLayerGroup(): L.LayerGroup
  sync(context: MarkerArcSyncContext): void
  rebuildNow(): void
  dispose(): void
}

export function createMarkerArcConnector(layerId: string): MarkerArcConnector {
  const layerGroup = L.layerGroup()
  let lastContext: MarkerArcSyncContext | null = null

  return {
    getLayerGroup() {
      return layerGroup
    },
    sync(context) {
      lastContext = context
      rebuildMarkerArcLayerGroup(layerId, layerGroup, context)
    },
    rebuildNow() {
      if (!lastContext) return
      rebuildMarkerArcLayerGroup(layerId, layerGroup, lastContext)
    },
    dispose() {
      layerGroup.clearLayers()
      lastContext = null
    },
  }
}
